import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Amplify, Auth } from 'aws-amplify'
import { useFeatures } from 'flagged'
import { Box, Container, Grid } from '../../components/mui/Core/index'
import { GenericSnackbar } from '../../components/GenericSnackbar/generic-snackbar'
import { setSnackbar } from '../../components/GenericSnackbar/generic-snackbar-slice'
import { getAuthenticatedUserInfo } from '../../api/userauthentication/get-authenticated-user'
import GenericNav from '../../components/genericcomponents/navbars/GenericNav'
import updateUserName from '../../api/userauthentication/updateUserName'
import { setAuthenticatedUser } from '../userauth/login/LogIn/log-in-slice'
import awsconfig from '../../aws-exports'
import { getAmplifyPasswordExceptionMessage } from '../../utils/aws-cognito-utils'
import { businessMessages } from '../../constants/business-message-constants'
import Constants from '../../constants/en.json'
import useBrowserTabTitles from '../../hooks/browserTabTitles/browser-tab-titles'
import PersonalInformationCard from './components/PersonalInformationCard/personal-information-card'
import PasswordCard from './components/PasswordCard/password-card'
import { changeUserAttribute } from '../../api/user/change-user-attribute'
import { authenticateUser } from '../../api/user/authentication'
import { changeExistingPassword } from '../../api/user/changepassword'
import PageTitle from '../../components/PageTitle/page-title'

Amplify.configure(awsconfig)

function AccountSetting() {
  useBrowserTabTitles(Constants.accountSettings.ACCOUNT_SETTINGS)
  const dispatch = useDispatch()
  const features = useFeatures()
  const isUserServiceEnabled = features.isUserServiceEnabled
  const authenticatedUser = useSelector((state) => state.login.authenticatedUser)

  const fetchUserInfo = useCallback(async () => {
    const user = await getAuthenticatedUserInfo()
    if (user) {
      dispatch(setAuthenticatedUser(user))
    }
  }, [dispatch])

  const handleSubmitUpdatedName = (user, handleClose) => {
    if (!isUserServiceEnabled) {
      updateUserName(user.firstName, user.lastName)
        .then(() => {
          fetchUserInfo()
          dispatch(
            setSnackbar({
              message: businessMessages.accountSettings.NAME_CHANGE_SUCCESS,
              severity: 'success',
              action: true,
            }),
          )
          if (handleClose) {
            handleClose()
          }
        })
        .catch(() => {
          dispatch(
            setSnackbar({
              message: businessMessages.accountSettings.NAME_CHANGE_EXCEPTION,
              severity: 'error',
              action: true,
            }),
          )
        })
    } else {
      const request = {
        userName: authenticatedUser.username,
        firstName: user.firstName,
        lastName: user.lastName,
      }
      changeUserAttribute(request)
        .then(() => {
          const updatedUser = {
            ...authenticatedUser,
            firstName: user.firstName,
            lastName: user.lastName,
          }
          dispatch(setAuthenticatedUser(updatedUser))
          dispatch(
            setSnackbar({
              message: businessMessages.accountSettings.NAME_CHANGE_SUCCESS,
              severity: 'success',
              action: true,
            }),
          )
          if (handleClose) {
            handleClose()
          }
        })
        .catch(() => {
          dispatch(
            setSnackbar({
              message: businessMessages.accountSettings.NAME_CHANGE_EXCEPTION,
              severity: 'error',
              action: true,
            }),
          )
        })
    }
  }

  const handleChangePasswordSuccess = (handleCloseModal) => {
    dispatch(
      setSnackbar({
        message: Constants.accountSettings.changePassword.CHANGE_PASSWORD_SUCCESS_MESSAGE,
        severity: 'success',
        action: true,
      }),
    )
    if (handleCloseModal) {
      handleCloseModal()
    }
  }

  const handleChangePasswordException = (exception) => {
    const message = exception?.message
      ? getAmplifyPasswordExceptionMessage(exception)
      : businessMessages.password.UNEXPECTED_EXCEPTION
    dispatch(
      setSnackbar({
        message,
        severity: 'error',
        action: true,
      }),
    )
  }

  const handleSubmitUpdatedPassword = async (
    newPassword,
    newPasswordAgain,
    currentPassword,
    handleCloseModal,
  ) => {
    if (newPassword === newPasswordAgain) {
      if (!features.isUserServiceEnabled) {
        await Auth.currentAuthenticatedUser().then((user) => {
          Auth.changePassword(user, currentPassword, newPassword)
            .then(() => handleChangePasswordSuccess(handleCloseModal))
            .catch((exception) => handleChangePasswordException(exception))
        })
      }
      if (features.isUserServiceEnabled) {
        const request = {
          username: authenticateUser.username,
          oldPassword: currentPassword,
          newPassword,
        }
        changeExistingPassword(request)
          .then(() => handleChangePasswordSuccess(handleCloseModal))
          .catch((exception) => handleChangePasswordException(exception))
      }
    } else {
      dispatch(
        setSnackbar({
          message: businessMessages.password.PASSWORD_MISMATCH,
          severity: 'error',
          action: true,
        }),
      )
    }
  }

  const columnStyle = { width: 'fit-content', paddingTop: '0px !important' }

  return (
    <Box>
      <GenericNav user='authenticated' selected='' />
      <Container maxWidth='xl'>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <PageTitle title={Constants.accountSettings.ACCOUNT_SETTINGS} />
          </Grid>
          <Grid sx={columnStyle}>
            <PersonalInformationCard handleSubmitUpdatedName={handleSubmitUpdatedName} />
            <PasswordCard handleSubmitUpdatedPassword={handleSubmitUpdatedPassword} />
          </Grid>
        </Grid>
      </Container>
      <GenericSnackbar />
    </Box>
  )
}

export default AccountSetting
